import {
  Square,
  Heading,
  Stack,
  Row,
  Column,
  Copy,
  Button,
  Flex,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import { ISearchParams, useLocationSearch } from 'src/utilities/useSearchHooks';
import { Pagination } from 'src/components/Pagination/Pagination';
import { Input } from 'src/components/Input/Input';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { getHomeBreadcrumb } from 'src/constants/urls';

interface SearchFormFields {
  locationQuery: string;
}
export const LOCATIONS_PAGE_SIZE = 10;

export default function EnterpriseLocations(): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();
  const currentQueryText = useRef(null);

  const { register, handleSubmit } = useForm<SearchFormFields>();

  const {
    doSearch,
    resultsList: locations,
    currentSearchPage,
    firstItemNumberFromPage,
    lastItemNumberFromPage,
    totalPages,
    totalResults,
    hasNoResultsWithoutQuery,
    hasNoResultsWithQuery,
  } = useLocationSearch(corpid as string, LOCATIONS_PAGE_SIZE);

  useEffect(() => {
    if (corpid) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid]);

  function searchLocations({ locationQuery }: SearchFormFields): void {
    const searchParams: ISearchParams = { page: 1 };

    if (locationQuery) {
      searchParams.query = locationQuery;
      currentQueryText.current = locationQuery;
    } else {
      currentQueryText.current = null;
    }

    doSearch(searchParams);
  }

  function getEmployeeCountString(employeeCount: number): string {
    return `${employeeCount} ${employeeCount === 1 ? 'Employee' : 'Employees'}`;
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Stack y="xxx-sm">
          <Heading variant="h1">Office Locations</Heading>
        </Stack>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpid),
            },
          ]}
        />
      </PageHeader>
      <Stack y="md">
        <PageSection>
          <Row gutter={24}>
            <Column phone={9} gutter={24}>
              <Heading variant="h4">
                Locations for Permissions and Invoicing
              </Heading>
              <Copy variant="body">
                Locations are used to create order permissions, which determine
                where your organization's employees may order food to. They also
                tie an employee to a location for reporting or invoicing
                purposes.
              </Copy>
            </Column>
            <Column phone={3} gutter={24} style={{ textAlign: 'center' }}>
              <RouterButtonLink
                href={`/corporate/enterprise/${corpid}/locations/create`}
                buttonVariant="primary"
                buttonSize="medium"
                useBlockButton
              >
                Add new location
              </RouterButtonLink>
            </Column>
          </Row>
        </PageSection>
      </Stack>
      <PageSection>
        <Stack y="xxx-sm">
          <Heading variant="h4">Active Locations</Heading>
        </Stack>
        <Stack y="x-sm">
          <Copy variant="body">
            {firstItemNumberFromPage} - {lastItemNumberFromPage} of{' '}
            {totalResults} Locations
          </Copy>
        </Stack>
        <Stack y="md" as="form">
          <Row>
            <Column phone={6}>
              <Row gutter={8}>
                <Column gutter={8} phone={8}>
                  <Input
                    ariaLabel="Search locations"
                    name="locationQuery"
                    id="locationQuery"
                    ref={register}
                    placeholder="Location Name"
                  />
                </Column>
                <Column gutter={8} phone={4}>
                  <Button
                    variant="primary"
                    block
                    buttonSize="medium"
                    onClick={handleSubmit(searchLocations)}
                  >
                    Search
                  </Button>
                </Column>
              </Row>
            </Column>
          </Row>
        </Stack>
        <Stack y="md">
          {hasNoResultsWithoutQuery && (
            <NoResultsMsg message="No results found." />
          )}
          {hasNoResultsWithQuery && (
            <NoResultsMsg
              message={`No results matching '${currentQueryText.current}'`}
            />
          )}
          {locations.map((location, index) => (
            <Square
              inset="md"
              style={{
                paddingLeft: 0,
              }}
              key={location.id}
              borderBottom={
                index === locations.length - 1 ? 'none' : '1px solid'
              }
              borderTop={index !== 0 ? 'none' : '1px solid'}
              borderColor="lightLine"
            >
              <Row gutter={12}>
                <Column phone={8} gutter={12}>
                  <Stack y="x-sm">
                    <Heading variant="h4">{location.name}</Heading>
                  </Stack>
                  <Stack y="xxx-sm">
                    {`${location.street_address1}${
                      location.street_address2
                        ? ` ${location.street_address2}`
                        : ''
                    }`}
                  </Stack>
                  <Stack y="xxx-sm">
                    {`${location.address_locality}, ${location.address_region} ${location.postal_code}`}
                  </Stack>
                  {!!location.employee_count && (
                    <Stack y="xxx-sm">
                      {getEmployeeCountString(location.employee_count)}
                    </Stack>
                  )}
                </Column>
                <Column phone={4} gutter={12}>
                  <Flex>
                    <Stack x="xx-lg">
                      <RouterButtonLink
                        href={`/corporate/enterprise/${corpid}/locations/${location.id}/employees`}
                        buttonVariant="tertiary"
                        buttonSize="medium"
                        useBlockButton
                      >
                        View employees
                      </RouterButtonLink>
                    </Stack>
                    <RouterButtonLink
                      href={`/corporate/enterprise/${corpid}/locations/${location.id}/edit`}
                      buttonVariant="tertiary"
                      buttonSize="medium"
                    >
                      Edit
                    </RouterButtonLink>
                  </Flex>
                </Column>
              </Row>
            </Square>
          ))}
        </Stack>
        {totalPages > 1 && (
          <Flex justifyContent="center">
            <Pagination
              pageSize={LOCATIONS_PAGE_SIZE}
              boundaryLinks
              page={currentSearchPage}
              collectionSize={totalResults}
              pageChange={(page) => {
                const searchParams: ISearchParams = { page };
                if (currentQueryText.current) {
                  searchParams.query = currentQueryText.current;
                }
                doSearch(searchParams);
              }}
            />
          </Flex>
        )}
      </PageSection>
    </Square>
  );
}
